<template>
  <div class="smartwatch-select">
    <Heading
      tag="h3"
      size="sm"
      class="smartwatch-select__heading"
    >
      {{ name }}
    </Heading>

    <DraggableScroller
      ref="draggableScrollbarElement"
      class="smartwatch-select__container"
      has-pagination
      show-single-active-pagination
      container-role="radiogroup"
      :container-label="name"
      :pagination-label="`${name} paginierung`"
    >
      <template v-for="(entry, key) of mappedEntries">
        <LinkButton
          :key="`${entry.value}-${key}`"
          tag="button"
          role="radio"
          :aria-checked="entry.active ? 'true' : 'false'"
          :secondary="entry.active && !entry.hasImage"
          :outline-primary="entry.active && entry.hasImage"
          class="smartwatch-select__entry"
          :class="{ 'smartwatch-select__entry--image': entry.hasImage }"
          small-padding
          :checked="entry.active && !entry.hasImage"
          @click="$emit('select', entry.value)"
        >
          <CdnImage
            v-if="entry.image"
            :src="entry.image"
            render-without-figure
          />
          {{ entry.label }}
        </LinkButton>
      </template>
    </DraggableScroller>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type PropType } from 'vue';
import { Heading, LinkButton } from '@i22/td-longtail-vue-components';
import DraggableScroller from '~/components/draggable-scroller.vue';
import CdnImage from '~/components/cdn-image.vue';

export type SelectEntry = {
  label: string;
  value: string | number;
  active?: boolean;
  image?: string | undefined;
};

type SelectEntryWithImage = SelectEntry & {
  hasImage: boolean;
};

const props = defineProps({
  name: {
    type: String,
    default: undefined,
  },
  entries: {
    type: Array as PropType<SelectEntry[]>,
    default: () => [],
  },
});

defineEmits<{
  (e: 'select', value: string | number): void;
}>();

const draggableScrollbarElement = ref(null);

const updateScroller = () => {
  if (!draggableScrollbarElement.value) return;
  draggableScrollbarElement.value.updateScroller();
};

const mappedEntries = computed<SelectEntryWithImage[]>(() =>
  props.entries.map((entry) => ({
    ...entry,
    hasImage: !!entry.image,
  }))
);

defineExpose({ updateScroller });
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.smartwatch-select {
  &__heading {
    margin-bottom: $grid-base;
  }

  &__container {
    overflow-x: auto;
  }

  :deep(.draggable-scroller__container) {
    display: flex;
    grid-gap: $grid-base;
    padding: $grid-base;
    max-width: calc(100% - 24px);
  }

  &__entry {
    &--image {
      --radius-full: 1rem;
      height: 150px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      :deep(img) {
        height: calc(100% - 41px);
        aspect-ratio: 1;
        max-width: none;
      }
    }
  }
}
</style>
